import { HttpContextToken, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';

export interface TransferHttpResponse {
  body?: unknown | null;
  headers?: Record<string, string[]>;
  status?: number;
  statusText?: string;
  url?: string;
}

// Set TTL in seconds for the cached response, default of `0` means caching disabled
export const RESPONSE_CACHE_TTL = new HttpContextToken<number>(() => 0);

// Internal fake header used by GraphQL fetch calls for providing a cache TTL
export const GQL_RESPONSE_CACHE_TTL_HEADER = 'gql-response-cache-ttl';

export function getHeadersMap(headers: HttpHeaders): Record<string, string[]> {
  const headersMap: Record<string, string[]> = {};
  for (const key of headers.keys()) {
    const values = headers.getAll(key);
    if (values !== null) {
      headersMap[key] = values;
    }
  }

  return headersMap;
}

export function createHttpResponse(response: TransferHttpResponse): HttpResponse<unknown> {
  return new HttpResponse<unknown>({
    body: response.body,
    headers: new HttpHeaders(response.headers),
    status: response.status,
    statusText: response.statusText,
    url: response.url
  });
}

export function serializeHttpResponse(response: HttpResponse<unknown>, includeHeaders = true): TransferHttpResponse {
  const headers = includeHeaders ? { headers: getHeadersMap(response.headers) } : {};
  return {
    body: response.body,
    ...headers,
    status: response.status,
    statusText: response.statusText,
    url: response.url || ''
  };
}

/**
 * Generate a cache key for REST requests based on method, url and query params
 * @param method
 * @param url
 * @param params
 */
export function generateCacheKey({ method, url, params }: HttpRequest<unknown>): string {
  const encodedParams = params
    .keys()
    .sort()
    .map(k => `${k}=${params.getAll(k)}`)
    .join('&');

  return `${method === 'GET' ? 'G' : 'H'}.${url}?${encodedParams}`;
}

/**
 * Generate a cache key for GraphQL request, based on operation name and input variables
 * @param operationName
 * @param variables
 */
function generateGqlCacheKey({ body: { operationName, variables } }: HttpRequest<any>): string {
  return `GQL.${operationName}.${JSON.stringify(variables)}`;
}

/**
 * This method workarounds lack of providing HttpRequest context from Apollo HttpLink to HttpClient:
 * In order to pass on a cache TTL for GraphQL requests, a fake header will be used for forwarding
 * a cache TTL to the interceptor. The method extracts the numeric TTL value and returns it
 * together with a cloned HttpRequest having the cache header removed.

 * @param req
 */
function getGqlCacheTTL(req: HttpRequest<unknown>): [HttpRequest<unknown>, number] {
  const gqlTtl = req.headers.get(GQL_RESPONSE_CACHE_TTL_HEADER);
  if (gqlTtl) {
    req = req.clone({ ...req, headers: req.headers.delete(GQL_RESPONSE_CACHE_TTL_HEADER) });
  }
  return [req, gqlTtl && Number(gqlTtl)];
}

/**
 * Returns HttpRequest and, if configured, a cache TTL plus key
 * @param request
 */
export function getRequestCacheParams(request: HttpRequest<unknown>): [HttpRequest<unknown>, number?, string?] {
  const [req, gqlTtl] = getGqlCacheTTL(request);
  const ttl = req.context.get(RESPONSE_CACHE_TTL) || gqlTtl;
  const cacheKey = ttl && (gqlTtl ? generateGqlCacheKey(req) : generateCacheKey(req));

  return ttl ? [req, ttl, cacheKey] : [req];
}
